import { Suspense, useContext, useEffect, useRef, useCallback } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import Contexts from "./contexts/Contexts";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import { useGetUserData } from "./api/hooks/user";

import Layout from "./pages/Layout";
import EmptyLayout from "./components/Layout/EmptyLayout";
// Phase 4 Step 2: reverted from lazy to eager — saves ~800-1000ms on 4G.
// The lazy round-trip cost exceeds the ~7KB gz bundle savings.
import Index from "./pages/Games";

// Lazy load non-critical routes to improve FCP
// Using React.lazy() with dynamic imports reduces initial bundle size
const BetWrapper = lazyWithRetry(() => import("./components/BetWrapper"));
const Groups = lazyWithRetry(() => import("./pages/Groups"));
const GroupDetail = lazyWithRetry(() => import("./pages/Groups/GroupDetail"));
const MyTickets = lazyWithRetry(() => import("./pages/MyTickets"));
const Feed = lazyWithRetry(() => import("./pages/Feed"));
const Cart = lazyWithRetry(() => import("./pages/Cart"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const Orders = lazyWithRetry(() => import("./pages/Orders"));
const Notifications = lazyWithRetry(() => import("./pages/Notifications"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const MiniGameList = lazyWithRetry(() => import("./components/MiniGame/MiniGameList"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const RegistrationStep2 = lazyWithRetry(() => import("./pages/RegistrationStep2"));
const MiniGame2048 = lazyWithRetry(() => import("./pages/MiniGame/2048/2048"));
const MiniGameMatch3 = lazyWithRetry(() => import("./pages/MiniGame/Match3/Match3"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/PrivacyPolicy/Terms"));
const ResponsibleGaming = lazyWithRetry(() => import("./pages/ResponsibleGaming"));
const About = lazyWithRetry(() => import("./pages/About"));
const ComoFunciona = lazyWithRetry(() => import("./pages/ComoFunciona"));
const LargePrizePage = lazyWithRetry(() => import("./pages/LargePrize"));
const Contacts = lazyWithRetry(() => import("./pages/Contacts"));
const BolaoLanding = lazyWithRetry(() => import("./pages/BolaoLanding"));
const AffiliateDashboard = lazyWithRetry(() => import("./pages/AffiliateDashboard"));
const PartnersLanding = lazyWithRetry(() => import("./pages/PartnersLanding"));
const PartnerApplicationStatus = lazyWithRetry(() => import("./pages/PartnerApplicationStatus"));
// Bundle 4c — referral routes
const ReferralLanding = lazyWithRetry(() => import("./pages/ReferralLanding"));
const ProfileReferrals = lazyWithRetry(() => import("./pages/ProfileReferrals"));
// O11 — Employee magic-link onboarding (public, token IS the credential)
const EmployeeOnboarding = lazyWithRetry(() => import("./pages/EmployeeOnboarding"));
// Bundle 5b — withdrawal routes
const Withdrawal = lazyWithRetry(() => import("./pages/Withdrawal"));
const WithdrawalHistory = lazyWithRetry(() => import("./pages/WithdrawalHistory"));
// Gifting Flavor A — gifter's sent-gifts list
const SentGifts = lazyWithRetry(() => import("./pages/SentGifts"));
// Gifting Flavor A — recipient's received-gifts inbox
const ReceivedGifts = lazyWithRetry(() => import("./pages/ReceivedGifts"));
// Gifting Flavor C — public share-by-link claim (token IS the credential)
const GiftClaim = lazyWithRetry(() => import("./pages/GiftClaim"));
// Bundle 6.A — operator dashboard (code-split: shadcn/ui + Tailwind + framer-motion NOT in main bundle)
const OperatorApp = lazyWithRetry(() => import("./operator/OperatorApp"));

export const ProtectedRoute = ({ children }: any) => {
  const location = useLocation();
  const { userId, setIsLoginOpen, appLoading, setPostLoginRedirect } = useContext(Contexts);
  const previousPathRef = useRef<string>("");
  
  // 检查是否为有效的用户（userId不为0即可，包括访客用户）
  const isAuthenticated = userId !== null && userId !== 0;
  
  useEffect(() => {
    const currentPath = location.pathname + location.search + location.hash;
    // 只在路由真正变化且用户未登录（userId为null或0）时打开弹窗
    if (!appLoading && !isAuthenticated && currentPath !== previousPathRef.current) {
      // 记录被拦截前的路径，在登录成功后跳回
      setPostLoginRedirect(currentPath);
      setIsLoginOpen(true);
      previousPathRef.current = currentPath;
    }
  }, [location.pathname, location.search, location.hash, isAuthenticated, appLoading, setIsLoginOpen, setPostLoginRedirect]);
  
  if (appLoading) {
    return null; // Return null to avoid blocking FCP
  }
  if (!isAuthenticated) {
    // 保持当前路由不变，只显示登录弹窗
    return null;
  } else {
    return children;
  }
};

const AppRoutes = () => {
  const { pathname } = useLocation();
  const { setNavVisible, setAppLoading, setGlobalGameFilter } = useContext(Contexts);
  const { fetchUserData, loadCachedUserData } = useGetUserData();
  
  // Memoize initialization function to avoid recreating on each render
  const initializeApp = useCallback(async () => {
    // 第一步：立即从缓存加载用户数据（同步操作）
    // 这样可以立即显示缓存的用户信息，提升用户体验
    loadCachedUserData();
    
    // 第二步：立即关闭加载状态，允许页面渲染
    // 不等待 API 完成，避免阻塞首页渲染
    setAppLoading(false);
    
    // 第三步：在后台异步获取最新用户数据（不阻塞渲染）
    // 使用 requestIdleCallback 进一步延迟，确保不阻塞关键渲染
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => {
        fetchUserData().catch(error => {
          console.error("Failed to fetch user data:", error);
        });
      }, { timeout: 2000 });
    } else {
      // Fallback: 延迟执行但不要太久
      setTimeout(() => {
        fetchUserData().catch(error => {
          console.error("Failed to fetch user data:", error);
        });
      }, 100);
    }
  }, [loadCachedUserData, fetchUserData, setAppLoading]);
  
  useEffect(() => {
    initializeApp();
  }, [initializeApp]);

  // Memoize nav visibility update
  const updateNavVisibility = useCallback(() => {
    setNavVisible(true);
  }, [setNavVisible]);

  useEffect(() => {
    updateNavVisibility();
  }, [pathname, updateNavVisibility]);

  // CC_08: route-aware game-filter reset. The global game filter only applies on
  // the home ("/") and groups list ("/groups") views. Navigating anywhere else
  // (bolão/detail pages, profile, cart, …) clears it, so those views — and any
  // later return to home/list — start from "Todos" instead of a stale selection.
  useEffect(() => {
    const normalized =
      pathname !== "/" && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
    if (normalized !== "/" && normalized !== "/groups") {
      setGlobalGameFilter(null);
    }
  }, [pathname, setGlobalGameFilter]);
  // Ultra-lightweight fallback - empty div to avoid blocking FCP
  // This allows the page to render immediately while components load
  const LoadingFallback = () => null;
  
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route element={<Layout sideBar={true} />}>
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <Orders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:orderId"
            element={
              <ProtectedRoute>
                <Orders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/login"
            element={
              <ProtectedRoute>
                <Index />
              </ProtectedRoute>
            }
          />
          <Route
            path="/games/:id"
            element={
              <BetWrapper />
            }
          />
          <Route
            path="/groups/:id"
            element={
              <ProtectedRoute>
                <GroupDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/cart"
            element={<Cart />}
          />
          <Route path="/mini-games2048" element={<MiniGame2048 />} />
          <Route path="/mini-games-match3" element={<MiniGameMatch3 />} />

          <Route path="/mini-games" element={<MiniGameList />} />

          <Route
            path="/my-tickets"
            element={
              <ProtectedRoute>
                <MyTickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/feed"
            element={
              <ProtectedRoute>
                <Feed />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route element={<Layout sideBar={true} showFilter={true} />}>
          <Route path="/groups" element={<Groups />} />
        </Route>
        <Route element={<Layout sideBar={false} showFilter={true} />}>
          <Route path="/" element={<Index />} />
          <Route path="/bolao/:groupId" element={<BolaoLanding />} />
          <Route
            path="/afiliados"
            element={
              <ProtectedRoute>
                <AffiliateDashboard />
              </ProtectedRoute>
            }
          />

          <Route path="/parceiros" element={<PartnersLanding />} />
          <Route path="/parceiros/inscricao/:applicationId" element={<PartnerApplicationStatus />} />

          <Route path="/privacidade" element={<PrivacyPolicy />} />
          <Route path="/termos" element={<TermsOfService />} />
          <Route path="/jogo-responsavel" element={<ResponsibleGaming />} />
          <Route path="/about" element={<About />} />
          <Route path="/como-funciona" element={<ComoFunciona />} />
          <Route path="/contacts" element={<Contacts />} />
          {/* Bundle 4c — referral landing (public, captures mlm_ref query param + opens registration) */}
          <Route path="/r/:referralCode" element={<ReferralLanding />} />
          <Route
            path="/perfil/indicacoes"
            element={
              <ProtectedRoute>
                <ProfileReferrals />
              </ProtectedRoute>
            }
          />
          {/* Bundle 5c.A — large-prize reveal */}
          <Route
            path="/perfil/premio/:handoffId"
            element={
              <ProtectedRoute>
                <LargePrizePage />
              </ProtectedRoute>
            }
          />
          {/* Bundle 5b — withdrawal routes */}
          <Route
            path="/perfil/saque"
            element={
              <ProtectedRoute>
                <Withdrawal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/perfil/saques"
            element={
              <ProtectedRoute>
                <WithdrawalHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/perfil/presentes"
            element={
              <ProtectedRoute>
                <SentGifts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/perfil/recebidos"
            element={
              <ProtectedRoute>
                <ReceivedGifts />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Index />} />
        </Route>
        {/* Bundle 6.A — Operator dashboard (/operacao/*) — completely outside customer Layout */}
        {/* Code-split: loads shadcn/ui + Tailwind + framer-motion only when /operacao is accessed */}
        <Route path="/operacao/*" element={
          <Suspense fallback={null}>
            <OperatorApp />
          </Suspense>
        } />
        {/* O11 — Employee magic-link onboarding — chrome-free single-task page */}
        {/* Outside any Layout: no Header buttons or Footer nav competing with the form */}
        {/* Bare /convite (no token) falls through to the catch-all → home */}
        <Route path="/convite/:token" element={<EmployeeOnboarding />} />
        {/* Gifting Flavor C — public share-by-link claim, chrome-free single-task page */}
        {/* UNGATED by giftingEnabled on purpose: a gated route would fall through to */}
        {/* the catch-all and silently render the homepage. The API's 9000 drives the */}
        {/* "indisponíveis" screen instead. Bare /presente → catch-all → home. */}
        <Route path="/presente/:token" element={<GiftClaim />} />
        {/* Profile页面使用EmptyLayout */}
        {/* Bolão landing — uses full app shell (Header, Footer, FloatNav) */}
        <Route element={<EmptyLayout />}>
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          {/* Bundle 5c.A — /perfil is the PT-BR FloatNav destination; same page, same auth guard */}
          <Route
            path="/perfil"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/registration/step2"
            element={
              <ProtectedRoute>
                <RegistrationStep2 />
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
